import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { StyleSheet } from "react-native";
import Clipboard from "@react-native-community/clipboard";
import { useDispatch, useSelector } from "react-redux";

import {
  getUserFirstName,
  getUserId,
  getUserLastName,
  getUserProfilePictureUrl,
  getUserRole,
} from "@microdoing/shared/services/user/selectors";
import {
  cleanMessage,
  getChatMessagesRequest,
  likeMessage,
  seeConversation,
  sendMessage,
} from "@microdoing/shared/services/message/actions";
import { requestExternalUser } from "@microdoing/shared/services/externalProfile/actions";
import Header from "../Header";
import i18n from "@microdoing/shared/i18n";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import {
  getConversationId,
  getLoadingMessage,
  getMessagePage,
  getMessages,
  getMessagesChatRequesting,
  getReceivers,
  getReceiversWithoutUser,
  getSelectedConversation,
  getUserClientSelectedName,
} from "@microdoing/shared/services/message/selectors";
import { getAppState } from "@microdoing/shared/services/nav/selectors";
import { useLaunchCamera } from "../../FeedbackScreen/useLaunchCamera";
import { getFilesToUpload } from "@microdoing/shared/services/upload/selectors";
import { useState } from "react";
import { updateFilesToUpload } from "@microdoing/shared/services/upload/actions";
import { getTeam } from "@microdoing/shared/services/userCampaign/selectors";
import { recordAudio } from "../useRecordAudio";

import hasTitleConversationGroup from "app/utils/hasTitleConversationGroup";
import LoadMore from "../LoadMore";

import theme from "app/Theme/Theme";
import { InputChat } from "../InputChat";

import ActionSheet from "react-native-actionsheet";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { removeMessage } from "@microdoing/shared/services/message/actions";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import { Box } from "app/components/common/Box/MainBox/MainBox";
import Animated, {
  useAnimatedKeyboard,
  useAnimatedStyle,
} from "react-native-reanimated";
import Chat from "./Chat";

const ChatScreen = ({ navigation, route }) => {
  const [text, setText] = useState("");
  const [focusedMessage, setFocusedMessage] = useState(null);
  const [error, setError, openNativeModule] = useLaunchCamera({});

  const [
    errorRecord,
    setErrorRecord,
    takeAudio,
    onStartRecording,
    onStopRecording,
    pauseRecording,
    audioDuration,
    isAudioActivated,
  ] = recordAudio();

  const receivers = useSelector(getReceivers);
  const receiversWithoutUser = useSelector(getReceiversWithoutUser);
  const messagePage = useSelector(getMessagePage);
  const loadingMessage = useSelector(getLoadingMessage);
  const messages = useSelector(getMessages);
  const language = useSelector(getLanguage);
  const userFirstName = useSelector(getUserFirstName);
  const userLastName = useSelector(getUserLastName);
  const userPicture = useSelector(getUserProfilePictureUrl);
  const userId = useSelector(getUserId);
  const userRole = useSelector(getUserRole);
  const requesting = useSelector(getMessagesChatRequesting);
  const userSelectedClientName = useSelector(getUserClientSelectedName);
  const filesToUpload = useSelector(getFilesToUpload);
  const conversationId = useSelector(getConversationId);
  const conversation = useSelector(getSelectedConversation);
  const team = useSelector(getTeam);

  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(cleanMessage());
      dispatch(updateFilesToUpload([]));
    };
  }, []);

  useEffect(() => {
    const { seen, user, seenBy } = messages?.[0] || { user: {}, seenBy: {} };
    const isNotSeen =
      messages?.length && !seen && !seenBy?.[userId] && user?._id !== userId;

    // last message not read and not of the user
    if (isNotSeen) {
      dispatch(seeConversation(conversationId));
    }
  }, [conversationId, dispatch, messages, userId]);

  const sendMessageText = message => {
    dispatch(sendMessage({ message: message?.text }));
    setText("");
  };

  const handlePressActionSheet = index => {
    if (index === 0) {
      dispatch(removeMessage({ messageId: focusedMessage?._id }));
    } else if (index === 1) {
      Clipboard.setString(focusedMessage?.text);
    }
  };

  const navigateExternalProfile = useCallback(
    id => () => {
      if (receivers.length === 1) {
        dispatch(requestExternalUser({ userId: id }));
      }
    },
    [receivers.length],
  );

  const navigateToTeam = () => () => {
    navigation.navigate("TeamDetails", { team });
  };

  const onLoadEarlier = useCallback(() => {
    dispatch(
      getChatMessagesRequest({
        conversationId: conversationId,
        page: messagePage,
      }),
    );
  }, [conversationId, messagePage]);

  const handleCancelSelection = () => {
    dispatch(updateFilesToUpload([]));
  };

  const handlePressAudio = async () => {
    takeAudio();
  };

  const cancelAudio = () => {
    pauseRecording();
  };

  const sendAudio = () => {
    onStopRecording();
    setTimeout(() => {
      dispatch(sendMessage({ message: "" }));
    }, 500);
  };

  const options = useMemo(
    () => [i18n.t("unsend"), i18n.t("copy"), i18n.t("cancel")],
    [language],
  );
  const CANCEL_INDEX = 2;

  const bottomSheetRef = useRef(null);

  const handleDisplayUnsend = useCallback(() => {
    bottomSheetRef?.current?.show();
  }, [bottomSheetRef]);

  return (
    <SafeAreaView style={styles.main}>
      <Header
        testID="chat-back-button"
        hasBackArrow={!isWebDesktop}
        title={hasTitleConversationGroup(
          conversation,
          receivers.length > 1 ? team?.name : "",
          receivers[0]?.name,
        )}
        clientName={
          userRole === "superAdmin" || userRole === "microdoingAdmin"
            ? userSelectedClientName
            : ""
        }
        receivers={receivers}
        navigateProfile={
          receivers.length === 1
            ? navigateExternalProfile(receiversWithoutUser?.[0]?._id)
            : navigateToTeam()
        }
      />
      {requesting && <LoadMore />}
      <Chat
        messages={messages}
        handlePressAvatar={navigateExternalProfile}
        focusedMessage={focusedMessage}
        setFocusedMessage={setFocusedMessage}
        displayUnsend={handleDisplayUnsend}
        onLoadEarlier={onLoadEarlier}
        isLoadingEarlier={loadingMessage}
        requesting={requesting}
      />
      <InputChat
        placeholderTextColor={theme.colors["neutral.600"]}
        filesToUpload={filesToUpload}
        openNativeModule={openNativeModule}
        onTextChanged={setText}
        handlePressAudio={handlePressAudio}
        handleCancelSelection={handleCancelSelection}
        cancelAudio={cancelAudio}
        sendAudio={sendAudio}
        text={text}
        isAudioActivated={isAudioActivated}
        audioDuration={audioDuration}
        onSend={sendMessageText}
      />
      {!isWebDesktop && (
        <ActionSheet
          ref={bottomSheetRef}
          options={options}
          cancelButtonIndex={CANCEL_INDEX}
          destructiveButtonIndex={0}
          onPress={handlePressActionSheet}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  main: {
    position: "relative",
    flex: 1,
    backgroundColor: theme.colors["neutral.white"],
  },
});

export default ChatScreen;
